import { createAppStore } from '@app/core/stores';
import { TimelineTooltipPayload } from '../lib/timeline-tooltip-types';
import { clampVisibleTimeRange, VisibleTimeRange } from '../lib/visible-time-range';
import {
  QUALITY_STATION_STATUS_FILTER_VALUES,
  StationStatusFilterValue,
} from '../quality-result/filter-quality-stations-by-status';
import { MsTimeRange, parseReportWindowMs, visibleRangeToMs } from './time-range-ms';

/** Pointer position in viewport CSS pixels; used by the portaled timeline tooltip. */
export type ReportTimelineTooltipAnchor = {
  payload: TimelineTooltipPayload;
  clientX: number;
  clientY: number;
};

/** Minimal client coordinates for tooltip anchoring (SVG/React mouse events). */
export type ReportTooltipClientPoint = {
  clientX: number;
  clientY: number;
};

export type ReportViewState = {
  reportDomainMs: MsTimeRange | null;
  visibleRangeMs: MsTimeRange | null;
  stationStatusFilter: StationStatusFilterValue[];
  /** Active timeline issue/gap tooltip; rendered outside clipped chart scroll areas. */
  timelineTooltip: ReportTimelineTooltipAnchor | null;
};

type ReportViewActions = {
  /**
   * Sets the report time domain and resets the visible window to the full domain.
   * Call when a new report result is loaded (`data.from` / `data.to` change).
   */
  initializeFromReportWindow: (fromIso: string, toIso: string) => void;
  /**
   * Updates the visible window from brush/chart interactions; values are clamped to the current domain.
   */
  setVisibleRangeFromDates: (range: VisibleTimeRange) => void;
  /** Sets visible range to the full report domain. */
  resetVisibleRangeToDomain: () => void;
  setStationStatusFilter: (next: StationStatusFilterValue[]) => void;
  /** Shows or hides the stream timeline hover tooltip (viewport coordinates). */
  setTimelineTooltip: (next: ReportTimelineTooltipAnchor | null) => void;
  /** Shows the timeline tooltip at the given viewport point (avoids imperative `getState()` in charts). */
  showTimelineTooltipAtClientPoint: (
    point: ReportTooltipClientPoint,
    payload: TimelineTooltipPayload,
  ) => void;
  /** Dismisses the timeline tooltip only. */
  hideTimelineTooltip: () => void;
  /**
   * Resolves the brush/chart visible window in ms: store range when domain matches this report window,
   * otherwise the report window (covers the frame before sync and mismatched domains).
   */
  getEffectiveVisibleRangeMs: (fromIso: string, toIso: string) => MsTimeRange;
  /** Clears view state when leaving the report screen (singleton store). */
  resetViewState: () => void;
};

export type ReportViewStore = ReportViewState & ReportViewActions;

const defaultStationStatusFilter: StationStatusFilterValue[] = [
  ...QUALITY_STATION_STATUS_FILTER_VALUES,
];

const initialState: ReportViewState = {
  reportDomainMs: null,
  visibleRangeMs: null,
  stationStatusFilter: defaultStationStatusFilter,
  timelineTooltip: null,
};

/**
 * Route-scoped view state for the stream quality report (brush window and station-status chips).
 * Report payload stays in React props; this store only holds UI state.
 */
export const useReportViewStore = createAppStore<ReportViewStore>('ReportViewStore')(
  (set, get) => ({
    ...initialState,

    initializeFromReportWindow: (fromIso, toIso) => {
      const domain = parseReportWindowMs(fromIso, toIso);
      if (!domain) {
        return;
      }
      set({
        reportDomainMs: domain,
        visibleRangeMs: { fromMs: domain.fromMs, toMs: domain.toMs },
      });
    },

    setVisibleRangeFromDates: (range) => {
      const domain = get().reportDomainMs;
      if (!domain) {
        return;
      }
      const clamped = clampVisibleTimeRange(
        range.from.getTime(),
        range.to.getTime(),
        domain.fromMs,
        domain.toMs,
      );
      set({ visibleRangeMs: visibleRangeToMs(clamped) });
    },

    resetVisibleRangeToDomain: () => {
      const domain = get().reportDomainMs;
      if (!domain) {
        return;
      }
      set({ visibleRangeMs: { fromMs: domain.fromMs, toMs: domain.toMs } });
    },

    setStationStatusFilter: (next) => set({ stationStatusFilter: next }),

    setTimelineTooltip: (next) => set({ timelineTooltip: next }),

    showTimelineTooltipAtClientPoint: (point, payload) =>
      set({
        timelineTooltip: {
          payload,
          clientX: point.clientX,
          clientY: point.clientY,
        },
      }),

    hideTimelineTooltip: () => set({ timelineTooltip: null }),

    getEffectiveVisibleRangeMs: (fromIso, toIso) => {
      const { reportDomainMs, visibleRangeMs } = get();
      const rFrom = new Date(fromIso).getTime();
      const rTo = new Date(toIso).getTime();
      if (
        visibleRangeMs != null &&
        reportDomainMs != null &&
        reportDomainMs.fromMs === rFrom &&
        reportDomainMs.toMs === rTo
      ) {
        return { fromMs: visibleRangeMs.fromMs, toMs: visibleRangeMs.toMs };
      }
      return { fromMs: rFrom, toMs: rTo };
    },

    resetViewState: () =>
      set({
        reportDomainMs: null,
        visibleRangeMs: null,
        stationStatusFilter: [...QUALITY_STATION_STATUS_FILTER_VALUES],
        timelineTooltip: null,
      }),
  }),
);
